/**
 * Dialog a 2 step per aggiungere un indirizzo Reply-To verificato a un'entità (§11.1).
 *   Step 1: inserimento email (+ label/default) → POST request-otp (invia OTP, non salva).
 *   Step 2: inserimento OTP → POST confirm → salvataggio (verified).
 *
 * Riferimento: TDD Sprint 18 §8.7.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { CustomTextField } from '../../../../utils/StyledInputs';
import { DefaultContainedButton, DefaultOutlinedButton } from '../../../../utils/Buttons';
import { HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { usePopup } from '../../../../context/PopupContext';
import { replyToApi } from '../../../../services/newsletter/replyToApi';

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   entityId: string,
 *   onConfirmed: (email: string) => void,
 * }} props
 */
export function ReplyToOtpDialog({ open, onClose, entityId, onConfirmed }) {
  const { showPopup, setPopUpText } = usePopup();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState(false);

  const reset = () => {
    setStep(1);
    setEmail('');
    setLabel('');
    setIsDefault(false);
    setOtp('');
    setOtpError(false);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleRequestOtp = (e) => {
    e.preventDefault();
    if (!email) {
      showPopup('warning', 'Inserisci un indirizzo email');
      return;
    }
    setLoading(true);
    replyToApi
      .requestOtp(entityId, { email, label: label || undefined, is_default: isDefault })
      .then(() => {
        showPopup(true, 'Codice OTP inviato');
        setStep(2);
      })
      .catch((err) => {
        HttpErrorHandler(err, setPopUpText);
        showPopup(false);
      })
      .finally(() => setLoading(false));
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (!otp) {
      setOtpError(true);
      return;
    }
    setLoading(true);
    setOtpError(false);
    replyToApi
      .confirm(entityId, { email, otp })
      .then(() => {
        showPopup(true, 'Indirizzo verificato e salvato');
        onConfirmed(email);
        reset();
        onClose();
      })
      .catch((err) => {
        // OTP errato/scaduto: resta in step 2 con errore inline.
        setOtpError(true);
        HttpErrorHandler(err, setPopUpText);
        showPopup(false);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <DialogTitle textAlign="center">Aggiungi indirizzo Reply-To</DialogTitle>

      {step === 1 ? (
        <form onSubmit={handleRequestOtp}>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <CustomTextField
                  required
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(ev) => setEmail(ev.currentTarget.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  label="Etichetta (opzionale)"
                  value={label}
                  onChange={(ev) => setLabel(ev.currentTarget.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={isDefault} onChange={(ev) => setIsDefault(ev.target.checked)} />}
                  label="Imposta come predefinito per l'entità"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <DefaultOutlinedButton onClick={handleClose} disabled={loading}>
              Annulla
            </DefaultOutlinedButton>
            <DefaultContainedButton type="submit" disabled={loading}>
              Invia codice
            </DefaultContainedButton>
          </DialogActions>
        </form>
      ) : (
        <form onSubmit={handleConfirm}>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Inserisci il codice OTP inviato a <strong>{email}</strong>.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CustomTextField
                  required
                  label="Codice OTP"
                  value={otp}
                  error={otpError}
                  onChange={(ev) => setOtp(ev.currentTarget.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <DefaultOutlinedButton onClick={() => setStep(1)} disabled={loading}>
              Indietro
            </DefaultOutlinedButton>
            <DefaultContainedButton type="submit" disabled={loading}>
              Verifica e salva
            </DefaultContainedButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

ReplyToOtpDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entityId: PropTypes.string,
  onConfirmed: PropTypes.func.isRequired,
};

export default ReplyToOtpDialog;